/**
 * SCRUM-308 — Suscripciones como centro operativo: KPIs → filtros → listado → detalle/acciones.
 *
 * Auditoría del modelo real (ver también api.admin.billing.ts):
 * - No existe ciclo mensual/anual en sm_subscriptions (plan_id es solo el tier).
 *   No se fabrica ese filtro — no hay dato real detrás.
 * - billing_provider está NULL en el 100% de las filas reales; el único valor
 *   real de modalidad es billing_mode ("manual" hoy). Se filtra por billing_mode.
 * - next_charge_at está NULL en todas las filas reales; los buckets de
 *   vencimiento se calculan sobre valid_until (poblado) en el backend.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { AdminGlassPanel } from "./AdminGlassPanel";
import { BUSINESS_PROFILE_LABELS, type BusinessProfile } from "@/lib/business-profile";

interface Sub {
  tenant_id: string;
  tenant_name: string | null;
  business_profile: BusinessProfile | null;
  plan_id: string;
  billing_mode: string;
  billing_status: string;
  billing_amount_cents: number | null;
  valid_until: string | null;
  next_charge_at: string | null;
  next_retry_at: string | null;
  charge_retry_count: number;
  max_retries: number;
  dunning_count: number;
  grace_until: string | null;
  suspended_at: string | null;
  suspension_reason: string | null;
  last_charge_status: string | null;
  last_charge_at: string | null;
}

interface Kpis {
  total: number;
  active: number;
  past_due: number;
  suspended: number;
  cancelled: number;
  trialing: number;
  in_grace: number;
  overdue: number;
  due_7d: number;
  due_30d: number;
  payment_issue: number;
}

const PAGE_SIZE = 50;
const PLAN_OPTIONS = ["starter", "pro", "business", "enterprise"];
const STATUS_LABELS: Record<string, string> = {
  active: "Activa",
  past_due: "Pago pendiente",
  suspended: "Suspendida",
  cancelled: "Cancelada",
  trialing: "Trial",
};
const STATUS_BADGE: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  active: "default",
  past_due: "secondary",
  suspended: "destructive",
  cancelled: "outline",
  trialing: "secondary",
};

function fmtMoney(c: number | null) {
  return c == null ? "—" : new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(c / 100);
}

function fmtDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function KpiTile({ label, value, tone }: { label: string; value: number; tone?: "warn" | "danger" }) {
  return (
    <div className="rounded-xl border border-border bg-card/40 p-3">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p
        className={`text-xl font-bold tabular-nums ${
          tone === "danger" ? "text-rose-400" : tone === "warn" ? "text-amber-400" : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}

export function BillingAdminPanel() {
  const [subs, setSubs] = useState<Sub[]>([]);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [byPlan, setByPlan] = useState<{ plan_id: string; n: number }[]>([]);
  const [byProfile, setByProfile] = useState<{ business_profile: string; n: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [detail, setDetail] = useState<Sub | null>(null);

  const [q, setQ] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [profileFilter, setProfileFilter] = useState("all");
  const [modeFilter, setModeFilter] = useState("all");
  const [dueFilter, setDueFilter] = useState("all");

  const buildParams = useCallback(
    (offset: number) => {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (planFilter !== "all") params.set("plan", planFilter);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (profileFilter !== "all") params.set("businessProfile", profileFilter);
      if (modeFilter !== "all") params.set("billingMode", modeFilter);
      if (dueFilter !== "all") params.set("dueWithin", dueFilter);
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      return params;
    },
    [q, planFilter, statusFilter, profileFilter, modeFilter, dueFilter],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGetJson<{
        subscriptions: Sub[];
        hasMore: boolean;
        kpis: Kpis;
        byPlan: { plan_id: string; n: number }[];
        byProfile: { business_profile: string; n: number }[];
      }>(`/api/admin/billing?${buildParams(0)}`);
      setSubs(res.subscriptions);
      setHasMore(res.hasMore);
      setKpis(res.kpis);
      setByPlan(res.byPlan);
      setByProfile(res.byProfile);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await apiGetJson<{ subscriptions: Sub[]; hasMore: boolean }>(
        `/api/admin/billing?${buildParams(subs.length)}`,
      );
      setSubs((prev) => [...prev, ...res.subscriptions]);
      setHasMore(res.hasMore);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar más");
    } finally {
      setLoadingMore(false);
    }
  };

  const act = async (tenantId: string, action: string, days?: number) => {
    setBusy(tenantId + action);
    try {
      await apiSendJson("/api/admin/billing", "POST", { tenantId, action, days });
      toast.success("Acción aplicada");
      await load();
      setDetail(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setBusy(null);
    }
  };

  const modeOptions = useMemo(() => [...new Set(subs.map((s) => s.billing_mode))], [subs]);

  const dueStatus = (s: Sub): { label: string; tone?: "warn" | "danger" } | null => {
    if (!s.valid_until) return null;
    const days = Math.ceil((new Date(s.valid_until).getTime() - Date.now()) / 86_400_000);
    if (days < 0 && s.billing_status !== "cancelled") return { label: `Vencida hace ${-days}d`, tone: "danger" };
    if (days <= 7) return { label: `Vence en ${days}d`, tone: "danger" };
    if (days <= 30) return { label: `Vence en ${days}d`, tone: "warn" };
    return null;
  };

  const renderActions = (s: Sub, size: "sm" = "sm") => (
    <div className="flex flex-wrap gap-1">
      {s.billing_status !== "active" && (
        <Button
          size={size}
          variant="outline"
          className="text-xs"
          disabled={busy === s.tenant_id + "reactivate"}
          onClick={() => void act(s.tenant_id, "reactivate")}
        >
          Reactivar
        </Button>
      )}
      {s.billing_status !== "suspended" && (
        <Button
          size={size}
          variant="ghost"
          className="text-xs"
          disabled={busy === s.tenant_id + "suspend"}
          onClick={() => void act(s.tenant_id, "suspend")}
        >
          Suspender
        </Button>
      )}
      <Button
        size={size}
        variant="ghost"
        className="text-xs"
        disabled={busy === s.tenant_id + "extend_grace"}
        onClick={() => void act(s.tenant_id, "extend_grace", 7)}
      >
        +7d gracia
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="glass-card flex items-center gap-2 rounded-xl p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando suscripciones…
      </div>
    );
  }

  return (
    <AdminGlassPanel
      title="Suscripciones"
      description="Salud de la base de suscripciones — dónde intervenir, distribución por plan y nicho."
    >
      {/* KPIs */}
      {kpis && (
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          <KpiTile label="Activas" value={kpis.active} />
          <KpiTile label="Vencen en 7d" value={kpis.due_7d} tone={kpis.due_7d > 0 ? "danger" : undefined} />
          <KpiTile label="Vencen en 30d" value={kpis.due_30d} tone={kpis.due_30d > 0 ? "warn" : undefined} />
          <KpiTile label="Vencidas" value={kpis.overdue} tone={kpis.overdue > 0 ? "danger" : undefined} />
          <KpiTile label="En gracia" value={kpis.in_grace} tone={kpis.in_grace > 0 ? "warn" : undefined} />
          <KpiTile label="Suspendidas" value={kpis.suspended} tone={kpis.suspended > 0 ? "danger" : undefined} />
          <KpiTile label="Pago pendiente" value={kpis.past_due} tone={kpis.past_due > 0 ? "warn" : undefined} />
          <KpiTile label="Con problema de cobro" value={kpis.payment_issue} tone={kpis.payment_issue > 0 ? "danger" : undefined} />
          <KpiTile label="Canceladas" value={kpis.cancelled} />
          <KpiTile label="Total" value={kpis.total} />
        </div>
      )}

      {(byPlan.length > 0 || byProfile.length > 0) && (
        <div className="mb-4 grid gap-2 sm:grid-cols-2">
          <div className="rounded-lg border border-border/60 p-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Por plan: </span>
            {byPlan.map((p) => `${p.plan_id} (${p.n})`).join(" · ")}
          </div>
          <div className="rounded-lg border border-border/60 p-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Por nicho: </span>
            {byProfile.map((p) => `${BUSINESS_PROFILE_LABELS[p.business_profile as BusinessProfile] ?? p.business_profile} (${p.n})`).join(" · ")}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar workspace…" className="h-9 pl-8 text-sm" />
        </div>
        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="h-9 w-[130px] text-xs"><SelectValue placeholder="Plan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los planes</SelectItem>
            {PLAN_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-[150px] text-xs"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={profileFilter} onValueChange={setProfileFilter}>
          <SelectTrigger className="h-9 w-[160px] text-xs"><SelectValue placeholder="Nicho" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los nichos</SelectItem>
            {Object.entries(BUSINESS_PROFILE_LABELS).map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
        {modeOptions.length > 1 && (
          <Select value={modeFilter} onValueChange={setModeFilter}>
            <SelectTrigger className="h-9 w-[140px] text-xs"><SelectValue placeholder="Modalidad" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toda modalidad</SelectItem>
              {modeOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={dueFilter} onValueChange={setDueFilter}>
          <SelectTrigger className="h-9 w-[160px] text-xs"><SelectValue placeholder="Vencimiento" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Cualquier vencimiento</SelectItem>
            <SelectItem value="7">Vencen en 7 días</SelectItem>
            <SelectItem value="30">Vencen en 30 días</SelectItem>
            <SelectItem value="overdue">Vencidas</SelectItem>
            <SelectItem value="grace">En gracia</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {subs.length === 0 ? (
        <p className="text-sm text-muted-foreground">Sin resultados para los filtros actuales.</p>
      ) : (
        <>
          {/* Cards en mobile */}
          <div className="flex flex-col gap-2 md:hidden">
            {subs.map((s) => {
              const due = dueStatus(s);
              return (
                <button
                  key={s.tenant_id}
                  type="button"
                  onClick={() => setDetail(s)}
                  className="rounded-lg border border-border/60 bg-secondary/10 p-3 text-left text-sm"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{s.tenant_name ?? s.tenant_id}</p>
                      <p className="text-xs text-muted-foreground">
                        {s.plan_id} · {s.business_profile ? BUSINESS_PROFILE_LABELS[s.business_profile] : "—"}
                      </p>
                    </div>
                    <Badge variant={STATUS_BADGE[s.billing_status] ?? "outline"} className="shrink-0">
                      {STATUS_LABELS[s.billing_status] ?? s.billing_status}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Vence {fmtDate(s.valid_until)}
                    {due && <span className={`ml-1 ${due.tone === "danger" ? "text-rose-400" : "text-amber-400"}`}>· {due.label}</span>}
                  </p>
                  <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                    {renderActions(s)}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Tabla en desktop */}
          <div className="hidden overflow-x-auto rounded-xl border border-border/60 md:block">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="p-2">Workspace</th>
                  <th className="p-2">Nicho</th>
                  <th className="p-2">Plan</th>
                  <th className="p-2">Modalidad</th>
                  <th className="p-2">Estado</th>
                  <th className="p-2">Vencimiento</th>
                  <th className="p-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => {
                  const due = dueStatus(s);
                  return (
                    <tr
                      key={s.tenant_id}
                      className="cursor-pointer border-t border-border/40 hover:bg-secondary/20"
                      onClick={() => setDetail(s)}
                    >
                      <td className="p-2">
                        <p className="font-medium">{s.tenant_name ?? s.tenant_id}</p>
                        {s.suspension_reason && <p className="text-[10px] text-muted-foreground">{s.suspension_reason}</p>}
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">
                        {s.business_profile ? BUSINESS_PROFILE_LABELS[s.business_profile] : "—"}
                      </td>
                      <td className="p-2">{s.plan_id}</td>
                      <td className="p-2 text-xs text-muted-foreground">{s.billing_mode}</td>
                      <td className="p-2">
                        <Badge variant={STATUS_BADGE[s.billing_status] ?? "outline"}>
                          {STATUS_LABELS[s.billing_status] ?? s.billing_status}
                        </Badge>
                      </td>
                      <td className="p-2 text-xs">
                        {fmtDate(s.valid_until)}
                        {due && <span className={`block ${due.tone === "danger" ? "text-rose-400" : "text-amber-400"}`}>{due.label}</span>}
                      </td>
                      <td className="p-2" onClick={(e) => e.stopPropagation()}>
                        {renderActions(s)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div className="flex justify-center pt-3">
              <Button variant="outline" size="sm" onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                Cargar más
              </Button>
            </div>
          )}
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{detail?.tenant_name ?? detail?.tenant_id}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <span className="text-muted-foreground">Nicho</span>
                <span>{detail.business_profile ? BUSINESS_PROFILE_LABELS[detail.business_profile] : "—"}</span>
                <span className="text-muted-foreground">Plan</span>
                <span>{detail.plan_id}</span>
                <span className="text-muted-foreground">Modalidad</span>
                <span>{detail.billing_mode}</span>
                <span className="text-muted-foreground">Estado</span>
                <span>{STATUS_LABELS[detail.billing_status] ?? detail.billing_status}</span>
                <span className="text-muted-foreground">Monto</span>
                <span>{fmtMoney(detail.billing_amount_cents)}</span>
                <span className="text-muted-foreground">Vence</span>
                <span>{fmtDate(detail.valid_until)}</span>
                <span className="text-muted-foreground">Gracia hasta</span>
                <span>{fmtDate(detail.grace_until)}</span>
                <span className="text-muted-foreground">Reintentos</span>
                <span>{detail.charge_retry_count}/{detail.max_retries}</span>
                <span className="text-muted-foreground">Dunning</span>
                <span>{detail.dunning_count}</span>
                <span className="text-muted-foreground">Último cobro</span>
                <span>{detail.last_charge_status ?? "—"} · {fmtDate(detail.last_charge_at)}</span>
                {detail.suspension_reason && (
                  <>
                    <span className="text-muted-foreground">Motivo suspensión</span>
                    <span>{detail.suspension_reason}</span>
                  </>
                )}
              </div>
              <div className="border-t border-border/60 pt-3">{renderActions(detail)}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminGlassPanel>
  );
}
